import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import PurchaseInvoiceModal from "./PurchaseInvoiceModal"
import PurchaseInvoiceShowModal from "./PurchaseInvoiceShowModal"
import { destroyExpense } from "../api/expenses"
import { CATEGORY_COLORS } from "../../../quotes/form/qlines/shared/category_styles"
import { TypeChip } from "../helpers/costCategories"
import { fmt, dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const formatEuros = cents =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((cents || 0) / 100)

const GRID = "grid grid-cols-[120px_minmax(0,2fr)_minmax(0,1fr)_140px_56px] gap-3"

const CategoryPill = ({ category }) => {
  if (!category) return <span className="text-slate-400">—</span>

  return <TypeChip type={category} />
}

const CategoryStrip = ({ byCategory }) => (
  <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-4">
    {Object.entries(byCategory || {}).map(([category, money]) => (
      <div key={category} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[category] || "#6b7280" }} />
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium truncate">
            {I18n.t(`quotes.new.cost_category.${category}`)}
          </div>
          <div className="text-[15px] font-semibold tabular-nums text-slate-900">{fmt(money)}</div>
        </div>
      </div>
    ))}
  </div>
)

const ExpensesSection = ({ expenses: initial, purchaseInvoices, policies, pendingPurchaseOrderId, onPendingHandled }) => {
  const projectId    = initial?.project_id
  const canManage    = !!policies?.can_manage_expenses
  const canCreate    = !!purchaseInvoices?.can_create

  const [items, setItems]           = useState(initial?.items || [])
  const [total, setTotal]           = useState(initial?.total)
  const [byCategory, setByCategory] = useState(initial?.by_category || {})
  const [modalOpen, setModalOpen]   = useState(false)
  const [modalPurchaseOrderId, setModalPurchaseOrderId] = useState(null)
  const [showId, setShowId]         = useState(null)

  useEffect(() => {
    if (!pendingPurchaseOrderId) return

    setModalPurchaseOrderId(pendingPurchaseOrderId)
    setModalOpen(true)
    onPendingHandled && onPendingHandled()
  }, [pendingPurchaseOrderId])

  const recompute = (next) => {
    const totalCents = next.reduce((acc, it) => acc + (it.subtotal?.cents || 0), 0)
    const grouped = next.reduce((acc, it) => {
      const category = it.cost_category || "other"
      acc[category] = (acc[category] || 0) + (it.subtotal?.cents || 0)
      return acc
    }, {})
    const buckets = {}
    Object.keys(byCategory || { labor: 0, equipment: 0, materials: 0, subcontracting: 0, other: 0 }).forEach(category => {
      const cents = grouped[category] || 0
      buckets[category] = { cents, formatted: formatEuros(cents) }
    })
    setItems(next)
    setTotal({ cents: totalCents, formatted: formatEuros(totalCents) })
    setByCategory(buckets)
  }

  // A created purchase invoice produces a project cost line — surface it as a row
  // built from the invoice payload (no reload), clickable through to its show modal.
  const onInvoiceSaved = (invoice) => {
    const row = {
      id:               `pi-${invoice.id}`,
      description:      invoice.title,
      cost_category:    null,
      issue_date:       invoice.issue_date,
      subtotal:         invoice.subtotal,
      editable:         false,
      purchase_invoice: { id: invoice.id, title: invoice.title, path: invoice.path },
    }
    const next = items.some(it => it.purchase_invoice?.id === invoice.id)
      ? items.map(it => (it.purchase_invoice?.id === invoice.id ? row : it))
      : [row, ...items]
    recompute(next)
  }

  const handleDelete = (event, expense) => {
    event.stopPropagation()
    if (!confirm(I18n.t("projects.expenses.actions.confirm_destroy"))) return
    destroyExpense(projectId, expense.id).then(response => {
      if (response?.success) recompute(items.filter(it => it.id !== expense.id))
    })
  }

  const openCreate  = () => { setModalPurchaseOrderId(null); setModalOpen(true) }
  const closeCreate = () => { setModalOpen(false); setModalPurchaseOrderId(null) }

  const sortKey = (item) => parseInt(String(item.purchase_invoice?.id ?? item.id).replace(/\D/g, ""), 10) || 0

  const sorted = useMemo(() => {
    return [...items].sort((first, second) => {
      const dateFirst  = first.issue_date || ""
      const dateSecond = second.issue_date || ""
      if (dateSecond !== dateFirst) return dateSecond.localeCompare(dateFirst)
      return sortKey(second) - sortKey(first)
    })
  }, [items])

  return (
    <section id="section-expenses" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.expenses.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {I18n.t("projects.expenses.subtitle")}
            {total?.formatted && (
              <>
                {" — "}
                <span className="font-semibold text-slate-900">{fmt(total)}</span>
                {" "}
                <span className="text-slate-400">({I18n.t("projects.expenses.count", { count: items.length })})</span>
              </>
            )}
          </p>
        </div>
        {canCreate && (
          <ActionButton onClick={openCreate}>
            {I18n.t("projects.expenses.invoices.actions.new")}
          </ActionButton>
        )}
      </header>

      <CategoryStrip byCategory={byCategory} />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className={`${GRID} px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
          <div>{I18n.t("projects.expenses.table.date")}</div>
          <div>{I18n.t("projects.expenses.table.description")}</div>
          <div>{I18n.t("projects.expenses.table.category")}</div>
          <div className="text-right">{I18n.t("projects.expenses.table.amount")}</div>
          <div />
        </div>

        {sorted.length === 0 ? (
          <div className="px-6 py-10 text-center text-[13px] text-slate-500">
            {I18n.t("projects.expenses.empty")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sorted.map(expense => {
              const invoiceId = expense.purchase_invoice?.id
              return (
                <div
                  key       = {expense.id}
                  role      = {invoiceId ? "button" : undefined}
                  onClick   = {invoiceId ? () => setShowId(invoiceId) : undefined}
                  className = {`group ${GRID} px-4 py-3 items-center text-[12.5px] ${invoiceId ? "cursor-pointer hover:bg-slate-50/70" : ""}`}
                >
                  <div className="text-slate-700">{dateFmt(expense.issue_date)}</div>
                  <div className="min-w-0 flex items-center gap-1.5">
                    <span className="text-slate-900 truncate" title={expense.description}>{expense.description || "—"}</span>
                    {invoiceId && <FontAwesomeIcon icon="eye" className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />}
                  </div>
                  <div className="min-w-0"><CategoryPill category={expense.cost_category} /></div>
                  <div className="text-right tabular-nums text-slate-900 font-medium">{fmt(expense.subtotal)}</div>
                  <div className="flex items-center justify-end">
                    {canManage && expense.editable && (
                      <IconButton tone="danger" size="md" title={I18n.t("projects.expenses.actions.destroy")} onClick={(event) => handleDelete(event, expense)}>
                        <FontAwesomeIcon icon="trash" />
                      </IconButton>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <PurchaseInvoiceModal
        isOpen          = {modalOpen}
        projectId       = {projectId}
        purchaseOrderId = {modalPurchaseOrderId}
        onClose         = {closeCreate}
        onSaved         = {onInvoiceSaved}
      />

      <PurchaseInvoiceShowModal
        id      = {showId}
        isOpen  = {showId != null}
        onClose = {() => setShowId(null)}
      />
    </section>
  )
}

export default ExpensesSection
